/**
 * Badge — state, rendered so that colour is never the only signal.
 *
 * Every badge carries a glyph, and the border style encodes settledness:
 *   solid border   settled state
 *   dashed border  still moving (is-pending)
 *   double border  terminal failure (is-terminal-bad)
 *   dotted border  settled, but covers less than it claims (is-partial)
 *
 * That combination survives greyscale printing, a projector, and colour-blindness,
 * which matters because these badges are the thing an operator scans a 200-row run
 * list for.
 *
 * Use `StatusBadge` for run status and `LinkBadge` for grant state rather than
 * hand-rolling tones — the mapping lives here so every screen agrees.
 *
 * TWO KINDS OF MARK, and they are not the same object:
 *
 *   A STATE is something that can be wrong, or that changes without anyone editing it:
 *   run status, coverage, link state, bounce state, a schedule that would be refused.
 *   It is the bordered pill above, and it is the thing an operator scans for.
 *
 *   A CLASSIFICATION is a fact about the row that cannot be wrong and never changes on
 *   its own: a report shape, a recipient kind, a file format, a grant's issuer. It is
 *   `variant="plain"` — uppercase micro-text with its glyph, no border and no fill.
 *
 * They used to render identically, so on a /runs row the eye landed on four competing
 * pills of equal weight, of which one was a state that needed attention and three were
 * taxonomy. One state badge per cell, at most two per row, and the second is only ever a
 * qualifier of the first (`Sent` + `Part day`).
 *
 * `tone="accent"` is banned on every badge: teal marks what you can ACT on (rule 3), and
 * a teal pill on something you cannot click spends the accent for nothing.
 */

import type { ReactNode } from 'react';

import { formatCount, isStalledRun, linkState, misalignedNote, stalledFor } from '@/lib/format';
import type { Coverage } from '@/lib/format';
import type { EpochSeconds, LinkState, RunStatus } from '@/lib/types';

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'muted';
export type BadgeVariant = 'soft' | 'solid' | 'outline' | 'plain';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /**
   * `plain` is a classification MARK, not a pill: no border, no fill, no padding,
   * uppercase micro-text, glyph kept. Use it for report shape, recipient kind, file
   * format, grant kind, the default column-policy tier and any other fact about the row
   * that cannot be wrong.
   */
  variant?: BadgeVariant;
  /** Monospace mark shown before the label. Pass null to omit. */
  glyph?: string | null;
  /** Dashed border — the state is still changing. */
  pending?: boolean;
  /** Double border — terminal failure. */
  terminalBad?: boolean;
  /** Beaded 2px border — settled, but the thing it describes is incomplete. */
  partial?: boolean;
  /** Pulsing dot instead of a glyph, for "happening right now". */
  live?: boolean;
  title?: string;
  className?: string;
}

export function Badge({
  children,
  tone = 'neutral',
  variant = 'soft',
  glyph = null,
  pending = false,
  terminalBad = false,
  partial = false,
  live = false,
  title,
  className = '',
}: BadgeProps) {
  const classes = [
    'badge',
    `tone-${tone}`,
    variant === 'solid' ? 'badge-solid' : '',
    variant === 'outline' ? 'badge-outline' : '',
    variant === 'plain' ? 'badge-plain' : '',
    pending ? 'is-pending' : '',
    terminalBad ? 'is-terminal-bad' : '',
    partial ? 'is-partial' : '',
    live ? 'is-live' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} title={title}>
      {/* Both, when there are both. These used to be alternatives, so a badge with a live
          dot silently dropped its glyph — and the glyph is the shape channel that makes a
          state readable when the colour is not: printed, projected, colour-blind, or
          screenshotted into a ticket. Rule 2 of this stylesheet is that state is never
          colour alone, and a pulsing dot is not a shape. */}
      {live ? <span className="badge-dot" aria-hidden="true" /> : null}
      {glyph ? (
        <span className="badge-glyph" aria-hidden="true">
          {glyph}
        </span>
      ) : null}
      <span className="badge-label">{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Run status (contract §2 nrd-runs)
// ---------------------------------------------------------------------------

interface StatusSpec {
  label: string;
  tone: BadgeTone;
  glyph: string;
  pending?: boolean;
  terminalBad?: boolean;
  live?: boolean;
  title: string;
}

const RUN_STATUS_SPEC: Record<RunStatus, StatusSpec> = {
  resolving: {
    label: 'Resolving',
    tone: 'info',
    glyph: '◔',
    pending: true,
    live: true,
    title: 'Locating the source object for the resolved window',
  },
  generating: {
    label: 'Generating',
    tone: 'info',
    glyph: '◑',
    pending: true,
    live: true,
    title: 'Reading the source object and building the artifact',
  },
  sending: {
    label: 'Sending',
    tone: 'info',
    glyph: '◕',
    pending: true,
    live: true,
    title: 'Minting grants and handing the message to SES',
  },
  sent: {
    label: 'Sent',
    tone: 'ok',
    glyph: '✓',
    title: 'Delivered to SES; links are live for the client',
  },
  // Not a failure of ours: the source was missing or older than the window, so
  // nothing was sent (contract §8.3). Its own state, never merged into failed.
  stale: {
    label: 'Stale',
    tone: 'warn',
    glyph: '◈',
    title: 'Source object missing or older than the window — nothing was sent',
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    glyph: '✕',
    terminalBad: true,
    title: 'The run errored. Nothing was delivered.',
  },
};

/**
 * Run status, plus — when `startedAt` is given — the measured fact that it has not moved.
 *
 * A run that cannot still be running must not animate. Three runs sat at "Generating" for
 * 19-23 hours presenting a live spinner, and nobody investigates a spinner: a stalled
 * dispatcher goes unnoticed until a client asks where their report is. The pulsing dead
 * run also looked HEALTHIER than a stale one, which at least gets an amber rule.
 *
 * This adds no status value and changes no stored field. RUN_STATUSES is a closed,
 * validated vocabulary; `stale` is never merged into `failed` and the status word is never
 * rewritten. Past STUCK_MINUTES an in-flight status renders static, warn, glyph ◷ — and
 * the elapsed time is stated beside the word as a separate dim element, by the caller.
 */
export function StatusBadge({
  status,
  startedAt,
  now,
  className,
}: {
  status: RunStatus;
  /** The run's started_at. Given, an in-flight status past STUCK_MINUTES stops animating. */
  startedAt?: string | null;
  now?: Date;
  className?: string;
}) {
  const spec = RUN_STATUS_SPEC[status] ?? {
    label: status,
    tone: 'neutral' as BadgeTone,
    glyph: '·',
    title: status,
  };
  const stalled = Boolean(spec.live) && isStalledRun(status, startedAt, now);

  if (stalled) {
    // The status WORD is untouched. The elapsed time is a separate dim element under it,
    // in a container of its own so a narrow status cell cannot push it into the next
    // column: `Generating` over `no progress for 21 h`, wherever it is rendered.
    return (
      <span className="stack-tight" style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
        <Badge
          tone="warn"
          glyph="◷"
          title={`${spec.title} — but it has not moved in ${stalledFor(startedAt, now)}. The dispatcher may have died mid-run.`}
          className={className}
        >
          {spec.label}
        </Badge>
        <span className="xs dim">no progress for {stalledFor(startedAt, now)}</span>
      </span>
    );
  }

  return (
    <Badge
      tone={spec.tone}
      /* Both, not one. ◔ ◑ ◕ are specified for resolving / generating / sending and were
         thrown away at render, leaving three identical dashed grey pills in greyscale —
         and under prefers-reduced-motion the pulse is disabled too, so the only remaining
         difference was the word. */
      glyph={spec.glyph}
      pending={spec.pending}
      terminalBad={spec.terminalBad}
      live={spec.live}
      title={spec.title}
      className={className}
    >
      {spec.label}
    </Badge>
  );
}


// ---------------------------------------------------------------------------
// Source coverage (CONTRACT-COVERAGE, amending §8.3)
// ---------------------------------------------------------------------------

/**
 * "Part day" — the run read its source completely and the SOURCE stops early.
 *
 * Orthogonal to `status`, exactly like `is_test`: a partial run can be `sent`, and the
 * run vocabulary is closed and validated on write, so this is never a status value.
 *
 * FORM, NOT JUST COLOUR. The operator's question in a 200-row list is "can I send this
 * to a client?", and they are often reading a screenshot pasted into a ticket. So this
 * badge carries three independent signals: the warn hue, the ◧ half-filled glyph, and a
 * beaded 2px border that is not the dashed edge of an in-flight run nor the doubled edge
 * of a failure. Any one of the three survives on its own.
 *
 * The word is always "Part day", never a bare "Partial" — this console already says
 * "partial read" in two places and means a truncated read of the S3 object, which is the
 * opposite problem.
 */
export function CoverageBadge({
  coverage,
  className,
}: {
  coverage: Coverage;
  className?: string;
}) {
  if (!coverage.isPartial) return null;
  return (
    <Badge tone="warn" glyph="◧" partial title={coverage.summary} className={className}>
      {coverage.coveredTo ? `Part day → ${coverage.coveredToLabel}` : 'Part day'}
    </Badge>
  );
}

/**
 * The count of rows the reader could not put under the right column names. A reason to
 * distrust the file, so it is its own mark rather than a footnote: on fusion's 13 Sep
 * export that was 70,006 of 185,115 rows.
 */
export function MisalignedBadge({
  coverage,
  className,
}: {
  coverage: Coverage;
  className?: string;
}) {
  const n = coverage.rowsMisaligned;
  if (n === null || n <= 0) return null;
  return (
    <Badge
      tone="danger"
      glyph="⊞"
      title={misalignedNote(coverage)}
      className={className}
    >
      {formatCount(n)} misaligned
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Link (grant) state — contract §1
// ---------------------------------------------------------------------------

const LINK_SPEC: Record<LinkState, { label: string; tone: BadgeTone; glyph: string; terminalBad?: boolean }> = {
  live: { label: 'Live', tone: 'ok', glyph: '●' },
  expiring: { label: 'Expiring', tone: 'warn', glyph: '◐' },
  expired: { label: 'Expired', tone: 'muted', glyph: '⊘' },
  revoked: { label: 'Revoked', tone: 'danger', glyph: '⊗', terminalBad: true },
};

/**
 * Grant state from OUR expires_at, never an S3 signature (contract §1).
 * Pass the grant; the countdown becomes the tooltip.
 */
export function LinkBadge({
  grant,
  showCountdown = false,
  className,
}: {
  grant: { expires_at?: EpochSeconds | null; revoked_at?: EpochSeconds | null };
  /** Render "Expires in 3 d" as the label instead of the bare state. */
  showCountdown?: boolean;
  className?: string;
}) {
  const status = linkState(grant);
  const spec = LINK_SPEC[status.state];
  return (
    <Badge
      tone={spec.tone}
      glyph={spec.glyph}
      terminalBad={spec.terminalBad}
      title={status.label}
      className={className}
    >
      {showCountdown ? status.label : spec.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Small shared badges
// ---------------------------------------------------------------------------

/**
 * active / inactive, used for clients, schedules and recipients alike.
 *
 * Inactive is SOLID, not dashed. Dashed means "still moving", and an inactive recipient is
 * the most settled state there is — the primitive everyone copies was teaching the wrong
 * grammar, which is how the meaning erodes on the rows where it matters.
 */
export function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <Badge tone="ok" glyph="●" title="Active">
      Active
    </Badge>
  ) : (
    <Badge tone="muted" glyph="○" title="Not active — nothing will be dispatched">
      Inactive
    </Badge>
  );
}

/** Column-policy tier (contract §6). */
export function PolicyBadge({ tier }: { tier: 'default' | 'opt_in' | 'never' }) {
  if (tier === 'default') {
    // A classification: this column ships, and that is the ordinary case. A pill here
    // competes with the tiers that carry consequence.
    return (
      <Badge variant="plain" glyph="·" title="Ships unless turned off">
        default
      </Badge>
    );
  }
  if (tier === 'opt_in') {
    return (
      <Badge tone="warn" glyph="◇" title="Ships only when the schedule lists it — call content">
        opt-in
      </Badge>
    );
  }
  /* A column you have deliberately chosen never to ship is a SETTING, not an incident.
     Danger plus a doubled border is reserved for `failed` and `revoked` — the two states
     that cannot be undone. A red double-bordered pill on every intentionally blocked
     column is decoration in a semantic hue, and after a day of it a genuine `Failed`
     stops registering. */
  return (
    <Badge tone="neutral" glyph="⊘" title="Unreachable by any selection">
      never
    </Badge>
  );
}

export default Badge;
